import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { getRevenueDashboardApi, type RevenueMetrics, type RevenueByCountry, type RevenueTrend } from '@/lib/billing'
import {
  LoaderIcon,
  TrendingUpIcon,
  BanknoteIcon,
  AlertTriangleIcon,
  GlobeIcon,
  CalendarIcon,
  UsersIcon,
  CreditCardIcon,
  DollarSignIcon,
  BarChart3Icon,
  ActivityIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from 'lucide-react'


export const Route = createFileRoute('/dashboard/admin/revenue')({
  component: () => {
    const [metrics, setMetrics] = useState<RevenueMetrics | null>(null)
    const [revenueByCountry, setRevenueByCountry] = useState<RevenueByCountry[]>([])
    const [monthlyTrend, setMonthlyTrend] = useState<RevenueTrend[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      let cancelled = false
      async function load() {
        try {
          const res = await getRevenueDashboardApi()
          if (!cancelled) {
            setMetrics(res.metrics)
            setRevenueByCountry(res.revenue_by_country)
            setMonthlyTrend(res.monthly_trend)
          }
        } catch (err) {
          if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load revenue data')
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [])

    if (loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Revenue dashboard</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading revenue data…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div>
              <h1 className="text-2xl font-semibold">Revenue dashboard</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Key subscription metrics and revenue breakdown.
              </p>
            </div>
          </div>
        </div>

        {!metrics ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <BarChart3Icon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No revenue data available</p>
            <p className="text-sm text-muted-foreground">Revenue metrics will appear once invoices are generated and paid.</p>
          </div>
        ) : (
          <>
            {/* ── Key Metric Cards ── */}
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                label="MRR"
                value={formatCentsWithCurrency(metrics.mrr_cents)}
                subtitle="Monthly recurring revenue"
                icon={TrendingUpIcon}
                color="text-emerald-500"
              />
              <MetricCard
                label="ARR"
                value={formatCentsWithCurrency(metrics.arr_cents)}
                subtitle="Annual recurring revenue"
                icon={TrendingUpIcon}
                color="text-blue-500"
              />
              <MetricCard
                label="Outstanding AR"
                value={formatCentsWithCurrency(metrics.outstanding_ar_cents)}
                subtitle={`${metrics.unpaid_invoices} unpaid invoices`}
                icon={metrics.outstanding_ar_cents > 0 ? AlertTriangleIcon : BanknoteIcon}
                color={metrics.outstanding_ar_cents > 0 ? 'text-amber-500' : 'text-emerald-500'}
              />
              <MetricCard
                label="Avg revenue/org"
                value={metrics.avg_revenue_per_org || '$0.00'}
                subtitle={`${metrics.active_subscriptions} active subscriptions`}
                icon={DollarSignIcon}
                color="text-purple-500"
              />
            </div>

            {/* ── Subscription Breakdown ── */}
            <div className="mb-6">
              <SectionHeading>Subscription breakdown</SectionHeading>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <MiniStatCard label="Active" value={String(metrics.active_subscriptions)} icon={UsersIcon} color="text-emerald-500" />
                <MiniStatCard label="Trial" value={String(metrics.trial_orgs)} icon={ClockIcon} color="text-blue-500" />
                <MiniStatCard label="Suspended" value={String(metrics.suspended_orgs)} icon={AlertTriangleIcon} color="text-destructive" />
                <MiniStatCard label="Complimentary" value={String(metrics.complimentary_orgs)} icon={CreditCardIcon} color="text-purple-500" />
              </div>
            </div>

            {/* ── Revenue by Country ── */}
            {revenueByCountry.length > 0 && (
              <div className="mb-6 rounded-xl border border-border bg-card p-5">
                <SectionHeading>Revenue by country</SectionHeading>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                        <th className="pb-2 pr-4">Country</th>
                        <th className="pb-2 pr-4">Currency</th>
                        <th className="pb-2 pr-4 text-right">Invoices</th>
                        <th className="pb-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueByCountry.map((r, i) => (
                        <tr key={r.iso_3 || i} className="border-b border-border/50 last:border-0">
                          <td className="py-2.5 pr-4">
                            <div className="flex items-center gap-2">
                              <GlobeIcon className="size-3.5 text-muted-foreground" />
                              <span className="font-medium text-foreground">{r.country}</span>
                              {r.iso_3 && <span className="text-[10px] text-muted-foreground">({r.iso_3})</span>}
                            </div>
                          </td>
                          <td className="py-2.5 pr-4 text-xs text-muted-foreground">{r.currency}</td>
                          <td className="py-2.5 pr-4 text-right text-xs tabular-nums">{r.invoice_count}</td>
                          <td className="py-2.5 text-right text-sm font-semibold tabular-nums">{formatCentsWithCurrency(r.revenue_cents, r.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Monthly Trend ── */}
            {monthlyTrend.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <SectionHeading>Monthly trend</SectionHeading>
                <div className="space-y-1">
                  {monthlyTrend.map((m) => (
                    <div key={m.month} className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-2">
                      <div className="flex size-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <CalendarIcon className="size-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground">{m.month}</p>
                        <p className="text-[10px] text-muted-foreground">{m.invoice_count} invoice{m.invoice_count !== 1 ? 's' : ''}</p>
                      </div>
                      <span className="text-sm font-semibold tabular-nums">{formatCentsWithCurrency(m.revenue_cents)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {revenueByCountry.length === 0 && monthlyTrend.length === 0 && (
              <div className="flex items-center justify-center rounded-xl border border-border bg-card py-12">
                <div className="flex flex-col items-center gap-2">
                  <ActivityIcon className="size-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No paid invoices yet. Revenue breakdown will appear here once invoices are paid.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )
  },
})

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <div className="flex items-center gap-[3px]" aria-hidden="true">
        <span className="inline-block w-[2px] h-3.5" style={{ backgroundColor: BRAND_MINT }} />
        <span className="inline-block rounded-full" style={{ width: '4px', height: '4px', backgroundColor: BRAND_MINT }} />
      </div>
      <h2 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-[0.12em] select-none">{children}</h2>
    </div>
  )
}

function MetricCard({ label, value, subtitle, icon: Icon, color }: {
  label: string; value: string; subtitle: string; icon: React.ElementType; color: string
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2">
        <Icon className={`size-5 shrink-0 ${color}`} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{subtitle}</span>
    </div>
  )
}

function MiniStatCard({ label, value, icon: Icon, color }: {
  label: string; value: string; icon: React.ElementType; color: string
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
      <Icon className={`size-5 shrink-0 ${color}`} />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-base font-semibold tabular-nums">{value}</p>
      </div>
    </div>
  )
}
